import { useState, useEffect, useCallback } from 'react';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { useAuth } from './useAuth';
import { useAccountLinks } from './useAccountLinks';

/**
 * Hook for user-related operations
 *
 * Manager/supervisor relationships are now managed through accountLinks.
 * Use useAccountLinks directly for:
 * - manager / directReports data
 * - sendLinkRequest with relationship type
 * - updateRelationship to change link type
 *
 * This hook provides:
 * - getUserByEmail / getUserByUid for lookups
 * - canViewUser / getViewableUsers for permission checks
 * - Legacy manager/supervisors props (delegated to accountLinks)
 */
export function useUsers() {
  const { user, userProfile } = useAuth();
  const {
    manager: accountLinkManager,
    directReports,
    activeLinks,
    loading: linksLoading
  } = useAccountLinks();

  // Convert accountLinks data to legacy format for backwards compatibility
  const [manager, setManager] = useState(null);
  const [supervisors, setSupervisors] = useState([]);
  const [loading, setLoading] = useState(true);

  // Sync manager from accountLinks
  useEffect(() => {
    if (linksLoading) {
      setLoading(true);
      return;
    }

    // Manager from accountLinks
    if (accountLinkManager) {
      setManager({
        id: accountLinkManager.userId,
        email: accountLinkManager.email,
        displayName: accountLinkManager.displayName
      });
    } else {
      setManager(null);
    }

    // Supervisors (direct reports) from accountLinks
    const supervisorList = directReports.map(report => ({
      id: report.userId,
      email: report.email,
      displayName: report.displayName
    }));
    setSupervisors(supervisorList);

    setLoading(false);
  }, [accountLinkManager, directReports, linksLoading]);

  // Get user by email
  const getUserByEmail = useCallback(async (email) => {
    const usersQuery = query(
      collection(db, 'users'),
      where('email', '==', email.toLowerCase())
    );
    const snapshot = await getDocs(usersQuery);
    if (snapshot.empty) return null;
    const docSnap = snapshot.docs[0];
    return { id: docSnap.id, ...docSnap.data() };
  }, []);

  // Get user by UID
  const getUserByUid = useCallback(async (uid) => {
    const userDoc = await getDoc(doc(db, 'users', uid));
    if (!userDoc.exists()) return null;
    return { id: userDoc.id, ...userDoc.data() };
  }, []);

  // Check if current user can view another user's data
  // Based on active account links with appropriate sharing
  const canViewUser = useCallback((targetUid) => {
    if (!user?.uid) return false;
    if (targetUid === user.uid) return true; // Own data

    // Check if there's an active link with this user
    const hasActiveLink = activeLinks.some(link => {
      const isUserA = link.userA === user.uid;
      const linkedUserId = isUserA ? link.userB : link.userA;
      return linkedUserId === targetUid;
    });

    return hasActiveLink;
  }, [user?.uid, activeLinks]);

  // Get all users that current user can view (self + linked users with sharing enabled)
  const getViewableUsers = useCallback(() => {
    const users = [];
    if (userProfile) {
      users.push(userProfile);
    }

    // Add all actively linked users
    activeLinks.forEach(link => {
      const isUserA = link.userA === user?.uid;
      const linkedUserId = isUserA ? link.userB : link.userA;
      const linkedUserEmail = isUserA ? link.userBEmail : link.userAEmail;
      const linkedUserDisplayName = isUserA ? link.userBDisplayName : link.userADisplayName;

      users.push({
        id: linkedUserId,
        email: linkedUserEmail,
        displayName: linkedUserDisplayName
      });
    });

    return users;
  }, [userProfile, activeLinks, user?.uid]);

  // Legacy functions - now deprecated, use useAccountLinks instead
  const addSupervisor = useCallback(async () => {
    console.warn('useUsers.addSupervisor is deprecated. Use useAccountLinks.sendLinkRequest with relationshipType instead.');
  }, []);

  const removeSupervisor = useCallback(async () => {
    console.warn('useUsers.removeSupervisor is deprecated. Use useAccountLinks.removeLink instead.');
  }, []);

  const setManagerForUser = useCallback(async () => {
    console.warn('useUsers.setManagerForUser is deprecated. Use useAccountLinks.sendLinkRequest with relationshipType instead.');
  }, []);

  const removeManager = useCallback(async () => {
    console.warn('useUsers.removeManager is deprecated. Use useAccountLinks.removeLink instead.');
  }, []);

  return {
    manager,
    supervisors,
    loading,
    getUserByEmail,
    getUserByUid,
    // Deprecated - use useAccountLinks directly
    addSupervisor,
    removeSupervisor,
    setManagerForUser,
    removeManager,
    // Permission helpers
    canViewUser,
    getViewableUsers
  };
}
